import { zodResolver } from '@hookform/resolvers/zod';
import { subYears } from 'date-fns';
import { UserCheck, Info, User, Building2, Heart, CalendarDays } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, FormProvider, type Resolver } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Autocomplete, { type AutocompleteItem } from '@/components/forms/autocomplete';
import AutoCompleteBirth from '@/components/forms/auto-complete-birth';
import SimpleDatePicker from '@/components/forms/simple-date-picker';
import RadioGroup from '@/components/forms/radio-group';
import Select from '@/components/forms/select';
import Text from '@/components/forms/text';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useUpdateTiersPayeur } from '@/hooks/api/use-contract-actions';
import { DATE_ISO, formatDate } from '@/lib/dates';
import { changeTiersPayeurSchema, type ChangeTiersPayeurValues } from '@/schemas/change-tiers-payeur';
import type { Contract, TiersPayeur } from '@/types/api/contract';
import { getCountries } from '@/lib/helpers';
import { tiersPayeurRelation } from '@/lib/options/select-options';

interface Country {
    id: string;
    name: string;
}

export interface ChangeTiersPayeurProps {
    contract: Contract;
}

const ChangeTiersPayeur = ({ contract }: ChangeTiersPayeurProps) => {
    const id_contrat = contract.id;
    const tiersPayeur = (contract.tiers_payeur ?? {}) as TiersPayeur;
    const { mutate: postUpdateTiersPayeur, isPending: loading } = useUpdateTiersPayeur();
    const initialValues: ChangeTiersPayeurValues = {
        type : (tiersPayeur.nom && (tiersPayeur.relation ? 'P' : 'M')) || 'P',
        firstname: tiersPayeur.prenom ?? '',
        lastname: tiersPayeur.nom ??'',
        birthname: tiersPayeur.nom_naissance ?? '',
        birthdate: tiersPayeur.naissance ? tiersPayeur.naissance : '',
        birthcity: tiersPayeur.ville_naissance ?? '',
        relationship: tiersPayeur.relation ?? '',
        third_payeur_reason: tiersPayeur.raison_autre ?? ''
    };
    const [countries, setCountries] = useState<AutocompleteItem[]>()
    const navigate = useNavigate();

    const methods = useForm<ChangeTiersPayeurValues>({
        defaultValues: initialValues,
        resolver: zodResolver(changeTiersPayeurSchema) as unknown as Resolver<ChangeTiersPayeurValues>,
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const { register, handleSubmit, watch, formState: { errors }, setValue } = methods;
    const values = watch();

    useEffect(() => {
        (async function fetchCountries(){
            const countries = await getCountries<Country[]>()

            const listcountries = countries.map(c => ({
                value: c.id,
                label: c.name
            }))

            setCountries(listcountries)
        })()
    }, [])

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm opacity-90 mb-1">Mon contrat</p>
                    <h1 className="text-3xl font-semibold flex items-center gap-3">
                        <UserCheck className="w-8 h-8" />
                        Tiers Payeur
                    </h1>
                </div>
            </div>

            {/* Info */}
            <Alert variant="warning">
                <Info className="w-4 h-4" />
                <AlertDescription>
                    Le tiers payeur est la personne qui va payer les cotisations tout en n'étant pas présente sur le contrat. Nous avons besoin de ses informations personnelles.
                </AlertDescription>
            </Alert>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(values => {postUpdateTiersPayeur({contractId: id_contrat, form: values, successFunction: () => navigate(`/mes-contrats/mon-contrat/${id_contrat}`)})})} className="space-y-6">
                    {/* Type de tiers payeur */}
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                {values.type === 'M'
                                    ? <Building2 className="w-5 h-5 text-primary" />
                                    : <User className="w-5 h-5 text-primary" />
                                }
                                Type de tiers payeur
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <RadioGroup
                                label={"Type de tiers payeur"}
                                name={'type'}
                                setValue={setValue}
                                options={[
                                    {value: "P", label:"Personne physique"},
                                    {value: "M", label:"Personne morale"}
                                ]}
                                value={values.type}
                                onChange={(e) => {
                                    if(e.target.value === "M"){
                                        setValue('firstname', '')
                                        setValue('birthname', '')
                                        setValue('birthdate', '')
                                        setValue('birthcountry', '')
                                        setValue('birthcity', '')
                                        setValue('relationship', '')
                                        setValue('third_payeur_reason', '')
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Lien avec le tiers payeur (personne physique uniquement) */}
                    {values.type === 'P' && (
                        <Card>
                            <CardHeader className="pb-6 border-b">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Heart className="w-5 h-5 text-primary" />
                                    Lien avec le tiers payeur
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-5">
                                <div className="max-w-md">
                                    <Select
                                        name={"relationship"}
                                        label={'Votre lien avec le tiers payeur'}
                                        value={values.relationship}
                                        error={errors.relationship?.message}
                                        onChange={e => {
                                            setValue('relationship', e.target.value)
                                            if(e.target.value !== 'A'){
                                                setValue('third_payeur_reason', '')
                                            }
                                        }}
                                    >
                                        <option value={""}>Sélectionnez une relation</option>
                                        {tiersPayeurRelation.map((relation, index) => (
                                            <option key={index} value={relation.code}>{relation.libelle}</option>
                                        ))}
                                    </Select>
                                </div>

                                {values.relationship === 'A' && (
                                    <div className="max-w-md">
                                        <Text
                                            type={'text'}
                                            label={'Motif d\'intervention du payeur'}
                                            {...register('third_payeur_reason')}
                                            value={values.third_payeur_reason}
                                            error={errors.third_payeur_reason?.message}
                                            maxLength={250}
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Identité */}
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                {values.type === 'M'
                                    ? <Building2 className="w-5 h-5 text-primary" />
                                    : <User className="w-5 h-5 text-primary" />
                                }
                                {values.type === 'P' ? 'Identité' : 'Informations de la société'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Text
                                    type={'text'}
                                    autoComplete={values.type === 'P' ? 'family-name' : 'organization'}
                                    label={values.type === 'P' ? 'Nom' : 'Nom de la société'}
                                    {...register('lastname')}
                                    value={values.lastname}
                                    error={errors.lastname?.message}
                                    normalize={(v: string) => v.toUpperCase()}
                                    maxLength={50}
                                />

                                {values.type === 'P' && (
                                    <Text
                                        type={'text'}
                                        autoComplete="given-name"
                                        label={'Prénom'}
                                        {...register('firstname')}
                                        value={values.firstname}
                                        error={errors.firstname?.message}
                                        normalize={(v: string) => v.toUpperCase()}
                                        maxLength={20}
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Naissance (personne physique uniquement) */}
                    {values.type === 'P' && (
                        <Card>
                            <CardHeader className="pb-6 border-b">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <CalendarDays className="w-5 h-5 text-primary" />
                                    Informations de naissance
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Text
                                        type={'text'}
                                        label={'Nom de naissance'}
                                        {...register('birthname')}
                                        value={values.birthname}
                                        error={errors.birthname?.message}
                                        normalize={(v: string) => v.toUpperCase()}
                                        maxLength={20}
                                    />

                                    <SimpleDatePicker
                                        label={'Date de naissance'}
                                        name={'birthdate'}
                                        setValue={(name, value) => setValue(name as 'birthdate', value)}
                                        value={values.birthdate as Date | string | null | undefined}
                                        error={errors.birthdate?.message}
                                        maxDate={new Date(formatDate(subYears(new Date(), 14), DATE_ISO))}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    {countries && (
                                        <Autocomplete
                                            name={`birthcountry`}
                                            label="Pays de naissance"
                                            data={countries}
                                            defaultValue={countries.find((country) => country.value === initialValues.birthcountry)?.label}
                                            setValue={setValue}
                                            error={errors.birthcountry?.message}
                                        />
                                    )}

                                    {values.birthcountry === 'FRA' ? (
                                        <AutoCompleteBirth
                                            name="birthcity"
                                            label='Ville de naissance'
                                            country={values.birthcountry}
                                            defaultValue={values.birthcity}
                                            setValue={setValue}
                                        />
                                    ) : (
                                        <Text
                                            type='text'
                                            label='Ville de naissance'
                                            {...register('birthcity')}
                                            value={values.birthcity}
                                            error={errors.birthcity?.message}
                                        />
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Action */}
                    <Button
                        type="submit"
                        className="w-full cursor-pointer"
                        loading={loading}
                        loadingText="Validation en cours..."
                    >
                        <UserCheck className="w-4 h-4" />
                        Je valide mon tiers payeur
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};
export default ChangeTiersPayeur;
